import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  CheckCircle2,
  XCircle,
  Sparkles,
  RotateCcw,
  Trophy,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { celebrate } from "@/lib/celebrate";

/**
 * Interactive scenario player.
 *
 * URL: /scenarios/:id
 *
 * State machine:
 *   - load scenario from API
 *   - render currentNode (start_node by default)
 *   - on option click → record pick + feedback overlay
 *   - on "Continue" → advance to next node, or end run
 *   - at ending node → submit run to backend, show summary
 */
export default function ScenarioPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [path, setPath] = useState([]);  // [{node_id, option_id}]
  const [pendingPick, setPendingPick] = useState(null); // {option, isLast}
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/scenarios/${id}`);
        if (cancelled) return;
        setScenario(r.data);
        setCurrentNodeId(r.data.start_node_id);
      } catch (err) {
        toast.error("Error al cargar el escenario");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const nodesById = useMemo(() => {
    if (!scenario) return {};
    return Object.fromEntries((scenario.nodes || []).map((n) => [n.id, n]));
  }, [scenario]);

  const currentNode = currentNodeId ? nodesById[currentNodeId] : null;
  const isEnding = !!currentNode?.ending || (currentNode && (!currentNode.options || currentNode.options.length === 0));

  const pickOption = (option) => {
    if (pendingPick) return;
    const isLast = !option.next_node_id || (nodesById[option.next_node_id]?.ending);
    setPendingPick({ option, isLast });
  };

  const confirmAndAdvance = async () => {
    if (!pendingPick) return;
    const { option } = pendingPick;
    const newPath = [...path, { node_id: currentNode.id, option_id: option.id }];
    setPath(newPath);
    setPendingPick(null);
    if (option.next_node_id) {
      setCurrentNodeId(option.next_node_id);
      const nextNode = nodesById[option.next_node_id];
      if (nextNode?.ending) {
        await finishRun(newPath, nextNode.ending_label || null);
      }
    } else {
      // no next → run ends here
      await finishRun(newPath, null);
    }
  };

  const finishRun = async (finalPath, endingLabel) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/scenarios/${id}/submit`, {
        path: finalPath,
        ending_label: endingLabel,
      });
      setResult(r.data);
      if (r.data.correct_picks >= 1) {
        celebrate("course_passed");
      }
      (r.data.points_awarded || []).forEach((p) => {
        toast.success(`+${p.points} pts · ${p.reason}`, { duration: 4500 });
        celebrate("points_earned");
      });
    } catch (err) {
      toast.error("Error al guardar el resultado");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setPath([]);
    setResult(null);
    setPendingPick(null);
    setCurrentNodeId(scenario?.start_node_id || null);
  };

  if (loading) return <div className="text-[#374151]">Cargando escenario...</div>;
  if (!scenario) return <div className="text-[#374151]">Escenario no encontrado.</div>;

  // ── Result summary ───────────────────────────────────────────────────────
  if (result) {
    const passed = result.correct_picks >= 1;
    return (
      <div className="max-w-3xl" data-testid="scenario-result">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A] mb-4">
          <ChevronLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
        <div className="bg-white border border-[#D1D5DB] rounded-xl p-8">
          <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-4 ${
            passed ? "bg-[#FEF3C7]" : "bg-[#FEE2E2]"
          }`}>
            {passed ? (
              <Trophy className="h-6 w-6 text-[#854D0E]" strokeWidth={1.8} />
            ) : (
              <AlertCircle className="h-6 w-6 text-[#991B1B]" strokeWidth={1.8} />
            )}
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium mb-1">
            Scenario complete
          </p>
          <h1 className="font-heading text-3xl text-[#1A1A1A] mb-1">{scenario.title}</h1>
          {result.ending_label && (
            <p className="text-sm text-[#374151] mb-6">Outcome: <strong>{result.ending_label}</strong></p>
          )}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="p-4 rounded-lg bg-[#F3F4F6]">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280] mb-1">Score</p>
              <p className="text-2xl font-heading text-[#1B2E7A]" data-testid="result-score">{result.score}</p>
            </div>
            <div className="p-4 rounded-lg bg-[#F3F4F6]">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280] mb-1">Correct picks</p>
              <p className="text-2xl font-heading text-[#1B2E7A]" data-testid="result-correct">
                {result.correct_picks}/{result.total_picks}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-[#F3F4F6]">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280] mb-1">Points earned</p>
              <p className="text-2xl font-heading text-[#1B2E7A]">
                {result.points_awarded?.length ? `+${result.points_awarded[0].points}` : "0"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-8 pt-6 border-t border-[#D1D5DB]">
            <Button
              onClick={reset}
              variant="outline"
              data-testid="scenario-retry"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" /> Try again
            </Button>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
              data-testid="scenario-finish"
            >
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Active scenario UI ───────────────────────────────────────────────────
  return (
    <div className="max-w-3xl" data-testid="scenario-player">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A] mb-4">
        <ChevronLeft className="h-3.5 w-3.5" /> Exit scenario
      </Link>

      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium mb-1">
          <Sparkles className="h-3 w-3 inline mr-1 text-[#F4B81F]" strokeWidth={2} />
          Sales scenario · Step {path.length + 1}
        </p>
        <h1 className="font-heading text-2xl text-[#1A1A1A]">{scenario.title}</h1>
        {scenario.description && (
          <p className="text-sm text-[#374151] mt-2 leading-relaxed">{scenario.description}</p>
        )}
      </div>

      {currentNode && (
        <div className="bg-white border border-[#D1D5DB] rounded-xl p-6 mb-4" data-testid="scenario-node">
          <p className="text-base text-[#1A1A1A] leading-relaxed mb-6 italic">
            &ldquo;{currentNode.prompt}&rdquo;
          </p>

          {!pendingPick && currentNode.options?.length > 0 && (
            <div className="space-y-2">
              {currentNode.options.map((opt, idx) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => pickOption(opt)}
                  className="w-full text-left p-4 rounded-lg border-2 border-[#E5E7EB] hover:border-[#1B2E7A] hover:bg-[#F3F4F6] transition-all group"
                  data-testid={`scenario-option-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-[#F3F4F6] group-hover:bg-[#1B2E7A] group-hover:text-white text-[#374151] text-xs flex items-center justify-center font-bold transition-colors shrink-0 mt-0.5">
                      {String.fromCharCode(65 + idx)}
                    </div>
                    <p className="text-sm text-[#1A1A1A] flex-1">{opt.text}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {pendingPick && (
            <div
              className={`rounded-lg p-5 border-2 ${
                pendingPick.option.is_correct
                  ? "border-[#1B2E7A] bg-[#EFF6FF]"
                  : "border-[#DC2626] bg-[#FEE2E2]"
              }`}
              data-testid="scenario-feedback"
            >
              <div className="flex items-start gap-3 mb-3">
                {pendingPick.option.is_correct ? (
                  <CheckCircle2 className="h-5 w-5 text-[#1B2E7A] mt-0.5 shrink-0" strokeWidth={2} />
                ) : (
                  <XCircle className="h-5 w-5 text-[#DC2626] mt-0.5 shrink-0" strokeWidth={2} />
                )}
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.16em] font-bold mb-1" style={{
                    color: pendingPick.option.is_correct ? "#1B2E7A" : "#991B1B"
                  }}>
                    {pendingPick.option.is_correct ? "Great choice" : "Not the best move"}
                  </p>
                  <p className="text-sm text-[#1A1A1A] mb-3">{pendingPick.option.text}</p>
                  {pendingPick.option.feedback && (
                    <p className="text-sm text-[#374151] italic">{pendingPick.option.feedback}</p>
                  )}
                  {pendingPick.option.points > 0 && (
                    <p className="text-xs text-[#854D0E] font-semibold mt-2">
                      +{pendingPick.option.points} pts
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={confirmAndAdvance}
                disabled={submitting}
                className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
                data-testid="scenario-continue"
              >
                {pendingPick.isLast ? "See result" : "Continue"} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Progress dots */}
      <div className="flex items-center gap-1.5 mt-2 px-1" data-testid="scenario-progress">
        {path.map((p, idx) => {
          const node = nodesById[p.node_id];
          const opt = node?.options?.find((o) => o.id === p.option_id);
          return (
            <span
              key={idx}
              className="h-2 w-6 rounded-full"
              style={{ backgroundColor: opt?.is_correct ? "#1B2E7A" : "#FCA5A5" }}
            />
          );
        })}
        <span className="h-2 w-6 rounded-full border border-[#D1D5DB]" />
      </div>
    </div>
  );
}
